/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { defineMessages } from 'react-intl';
export const AdvancedOptions = defineMessages({
  title: {
    id: 'phone.AdvancedOptions.title',
    defaultMessage: 'Advanced options'
  },
  description: {
    id: 'phone.AdvancedOptions.description',
    defaultMessage:
      'Need help? <link>Learn more</link> about the advanced options for the phone integration.'
  },
  customDeploymentDescription: {
    id: 'phone.AdvancedOptions.customDeploymentDescription',
    defaultMessage:
      'Need something more complex? <link>Contact us</link> for a custom deployment.'
  },
  subtitleOne: {
    id: 'phone.AdvancedOptions.subtitle1',
    defaultMessage: 'Call and transfer failures'
  },
  sipTargetLabel: {
    id: 'phone.AdvancedOptions.sipTargetLabel',
    defaultMessage: 'SIP target when a call fails'
  },
  sipTargetPlaceholder: {
    id: 'phone.AdvancedOptions.sipTargetPlaceholder',
    defaultMessage: 'For example: sip:18001234567@terminate.uri.net'
  },
  sipUsernameRequired: {
    id: 'phone.AdvancedOptions.sipUsernameRequired',
    defaultMessage: 'Username is required if using SIP authentication'
  },
  sipPasswordRequired: {
    id: 'phone.AdvancedOptions.sipPasswordRequired',
    defaultMessage: 'Password is required if using SIP authentication'
  },
  callFailureDescription: {
    id: 'phone.AdvancedOptions.callFailureDescription',
    defaultMessage:
      'The messages below determine what your users will hear in the event of a call or transfer failure. They are set by default and can be modified if desired.'
  },
  callFailureLabel: {
    id: 'phone.AdvancedOptions.callFailureLabel',
    defaultMessage: 'Call failure message'
  },
  callFailurePlaceholder: {
    id: 'phone.AdvancedOptions.callFailurePlaceholder',
    defaultMessage:
      "There's been a problem with the call. You’re being transferred to a live agent now."
  },
  transferFailureLabel: {
    id: 'phone.AdvancedOptions.transferFailureLabel',
    defaultMessage: 'Transfer failure message'
  },
  transferFailurePlaceholder: {
    id: 'phone.AdvancedOptions.transferFailurePlaceholder',
    defaultMessage:
      "Sorry, I couldn't connect you with a live agent. Please try again later. Goodbye."
  },
  transferFailurePrimary: {
    id: 'phone.AdvancedOptions.transferFailurePrimary',
    defaultMessage: 'Yes, turn off'
  },
  transferFailureSecondary: {
    id: 'phone.AdvancedOptions.transferFailureSecondary',
    defaultMessage: 'Cancel'
  },
  transferFailureModalLabel: {
    id: 'phone.AdvancedOptions.transferFailureModalLabel',
    defaultMessage: 'Warning'
  },
  transferFailureModalHeading: {
    id: 'phone.AdvancedOptions.transferFailureModalHeading',
    defaultMessage: 'Turn off Disconnect call on transfer failure?'
  },
  transferFailureModalBody: {
    id: 'phone.AdvancedOptions.transferFailureModalBody',
    defaultMessage:
      'It is important that the Assistant be configured with an Action or webhook that can handle this scenario. <link>Learn more</link>'
  },
  disconnectOnFailure: {
    id: 'phone.AdvancedOptions.disconnectOnFailure',
    defaultMessage: 'Disconnect call on transfer failure'
  },
  subtitleTwo: {
    id: 'phone.AdvancedOptions.subtitle2',
    defaultMessage: 'Security'
  },
  forceSecureTrunking: {
    id: 'phone.AdvancedOptions.forceSecureTrunking',
    defaultMessage: 'Force secure trunking'
  },
  moreInformation: {
    id: 'phone.AdvancedOptions.moreInformation',
    defaultMessage: 'More information'
  },
  secureTrunkingTooltip: {
    id: 'phone.AdvancedOptions.secureTrunkingTooltip',
    defaultMessage:
      'TLS must be used to encrypt SIP messages on port 5061, and SRTP must be used to encrypt the media packets. Any non-encrypted calls will be rejected.'
  },
  enableSipAuth: {
    id: 'phone.AdvancedOptions.enableSipAuth',
    defaultMessage: 'Enable SIP authentication'
  },
  usernameLabel: {
    id: 'phone.AdvancedOptions.usernameLabel',
    defaultMessage: 'Username'
  },
  usernamePlaceholder: {
    id: 'phone.AdvancedOptions.usernamePlaceholder',
    defaultMessage: 'Enter a username'
  },
  passwordLabel: {
    id: 'phone.AdvancedOptions.passwordLabel',
    defaultMessage: 'Password'
  },
  passwordPlaceholder: {
    id: 'phone.AdvancedOptions.passwordPlaceholder',
    defaultMessage: 'Enter a password'
  },
  subtitleFour: {
    id: 'phone.AdvancedOptions.subtitleFour',
    defaultMessage: 'Service Orchestration Engine'
  },
  enableSoe: {
    id: 'phone.AdvancedOptions.enableSoe',
    defaultMessage: 'Use Service Orchestration Engine'
  },
  enableSoeHelperText: {
    id: 'phone.AdvancedOptions.enableSoeHelperText',
    defaultMessage:
      'The Service Orchestration Engine is a dark feature providing support for migration from IBM Voice Agent.'
  },
  soeUrlLabel: {
    id: 'phone.AdvancedOptions.soeUrlLabel',
    defaultMessage: 'Url'
  },
  soeUrlPlaceholder: {
    id: 'phone.AdvancedOptions.soeUrlPlaceholder',
    defaultMessage: 'http://someurl.com'
  },
  soeSelect: {
    id: 'phone.AdvancedOptions.soeSelect',
    defaultMessage: 'Select'
  },
  soeSelectLabel: {
    id: 'phone.AdvancedOptions.soeSelectLabel',
    defaultMessage: 'Select a version'
  },
  soeSelectOptionOne: {
    id: 'phone.AdvancedOptions.soeSelectOptionOne',
    defaultMessage: 'v1'
  },
  soeSelectOptionTwo: {
    id: 'phone.AdvancedOptions.soeSelectOptionTwo',
    defaultMessage: 'v2'
  },
  soeUsernameLabel: {
    id: 'phone.AdvancedOptions.soeUsernameLabel',
    defaultMessage: 'Username'
  },
  soeUsernamePlaceholder: {
    id: 'phone.AdvancedOptions.soeUsernamePlaceholder',
    defaultMessage: 'Enter a username'
  },
  soePassword: {
    id: 'phone.AdvancedOptions.soePassword',
    defaultMessage: 'Password'
  },
  soeUsernameRequired: {
    id: 'phone.AdvancedOptions.soeUsernameRequired',
    defaultMessage: 'Username is required if using an SOE'
  },
  soePasswordRequired: {
    id: 'phone.AdvancedOptions.soePasswordRequired',
    defaultMessage: 'Password is required if using an SOE'
  },
  soeUrlRequired: {
    id: 'phone.AdvancedOptions.soeUrlRequired',
    defaultMessage: 'A valid URL is required if using an SOE'
  },
  FAILURE: {
    id: 'phone.AdvancedOptions.FAILURE',
    defaultMessage: 'Failure Fallback'
  },
  SIP_CALL_OPTIONS: {
    id: 'phone.AdvancedOptions.SIP_CALL_OPTIONS',
    defaultMessage: 'SIP call options'
  },
  SOE: {
    id: 'phone.AdvancedOptions.SOE',
    defaultMessage: 'Service Orchestration Engine'
  },
  customExtension: {
    id: 'phone.AdvancedOptions.customExtension',
    defaultMessage: 'Custom extension'
  },
  customExtensionAsync: {
    id: 'phone.AdvancedOptions.customExtensionAsync',
    defaultMessage: 'Provide user notification before calling custom extension'
  }
});
